import { conversionEventId, type ConversionMilestone } from '@shared/conversion-events';
import { trackOnce } from './analytics';
import { config } from './config';
import { supabase } from './supabase';

const SENT_KEY = 'cs_conversion_milestones_v1';

/**
 * The server re-checks the account or workspace and sends the Meta Conversions API event with the
 * browser event's ID. It also reaches Meta when an ad blocker stops the Pixel.
 */
async function notifyServer(milestone: ConversionMilestone, key: string, token: string) {
  let sent: string[] = [];
  try {
    sent = JSON.parse(localStorage.getItem(SENT_KEY) || '[]');
    if (!Array.isArray(sent)) sent = [];
  } catch {
    /* A repeat is harmless: Meta keeps the first event with this ID. */
  }
  if (sent.includes(key)) return;
  const response = await fetch(`${config.apiBase}/api/onboarding/milestone`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ milestone }),
  });
  if (!response.ok) return;
  try {
    localStorage.setItem(SENT_KEY, JSON.stringify([...sent, key].slice(-50)));
  } catch {
    /* Storage is optional. */
  }
}

async function report(
  milestone: ConversionMilestone,
  key: (userId: string) => string,
  event: string,
  params = {},
) {
  if (config.isDemo) return;
  try {
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    if (!session) return;
    const id = key(session.user.id);
    trackOnce(id, event, params);
    await notifyServer(milestone, id, session.access_token);
  } catch {
    /* Measurement never blocks signup or onboarding. */
  }
}

/** A verified account exists: Meta CompleteRegistration / GA4 sign_up. */
export const reportSignup = (params: { method: 'email' | 'google'; company_size?: string }) =>
  report('account_created', conversionEventId.signup, 'sign_up', params);
